import { useState, useRef, useEffect } from 'react';
import { Camera, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAdminEdit } from '@/contexts/AdminEditContext';
import { useCMSData } from '@/contexts/CMSDataContext';
import { uploadImage, adminApi } from '@/lib/adminApi';
import { toast } from 'sonner';

interface EditableImageProps {
  src: string;
  alt: string;
  className?: string;
  imageKey?: string; // Key to store/retrieve from CMS
  folder?: string; // Upload folder
  onImageChange?: (newSrc: string) => void;
}

const EditableImage = ({ 
  src, 
  alt, 
  className = "", 
  imageKey,
  folder = "uploads",
  onImageChange 
}: EditableImageProps) => {
  const { editMode } = useAdminEdit();
  const { globalData, isLoading } = useCMSData();
  const [isUploading, setIsUploading] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load saved image from shared CMS data
  useEffect(() => {
    if (!imageKey || isLoading || !globalData) return;
    
    const currentLocales = (globalData?.locales as any) || {};
    const savedImageUrl = currentLocales.en?.[imageKey] || currentLocales.hu?.[imageKey];
    
    if (savedImageUrl && savedImageUrl !== src) {
      setCurrentSrc(savedImageUrl);
      if (onImageChange) {
        onImageChange(savedImageUrl);
      }
    }
  }, [imageKey, src, onImageChange, globalData, isLoading]);

  const handleImageClick = () => {
    if (editMode && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    let uploadResult = null;
    
    try {
      // Upload the image first
      uploadResult = await uploadImage(file, folder);
      const newImageUrl = uploadResult.url;

      // Try to update the image in CMS if imageKey is provided
      if (imageKey) {
        try {
          // First get existing data
          const existing = await adminApi({
            action: 'list',
            model: 'global'
          });

          const currentItem = existing.item || {};
          const currentLocales = currentItem.locales || {};
          
          // Update locales for both languages with the new image URL
          const updatedLocales = {
            ...currentLocales,
            en: {
              ...currentLocales.en,
              [imageKey]: newImageUrl
            },
            hu: {
              ...currentLocales.hu,
              [imageKey]: newImageUrl
            }
          };

          await adminApi({
            action: 'upsert',
            model: 'global',
            data: {
              id: currentItem.id, // Include existing ID if present
              locales: updatedLocales,
              draft: false // Make it published so it persists
            }
          });
        } catch (dbError: any) {
          console.warn('Failed to save image reference to database:', dbError);
          // Continue with local update even if database save fails
        }
      }

      // Update local state
      setCurrentSrc(newImageUrl);
      
      // Call the callback if provided
      if (onImageChange) {
        onImageChange(newImageUrl);
      }

      toast.success('Image updated successfully');
    } catch (error: any) {
      console.error('Error uploading image:', error);
      
      // Check for authentication errors specifically
      const isAuthError = error.message === "Not authenticated" || 
                         error.message?.includes("Invalid or expired session") ||
                         (error.context && error.context.status === 401);
      
      if (isAuthError) {
        // Session expired, clear token and redirect to login
        localStorage.removeItem("admin_token");
        toast.error('Session expired. Please log in again.');
        console.log("Session expired, redirecting to login");
        window.location.href = "/portal-commodit-7842/login";
        return;
      }
      
      // If upload succeeded but database save failed, still update the image locally
      if (uploadResult?.url) {
        setCurrentSrc(uploadResult.url);
        if (onImageChange) {
          onImageChange(uploadResult.url);
        }
        toast.error('Image uploaded but may not persist. Database save failed.');
      } else {
        toast.error('Failed to upload image');
      }
    } finally {
      setIsUploading(false);
      // Reset the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="relative group">
      <img 
        src={currentSrc} 
        alt={alt} 
        className={className}
        loading="lazy"
        onError={(e) => {
          // Fallback to original src if current fails
          if (currentSrc !== src) {
            setCurrentSrc(src);
          }
        }}
      />
      
      {editMode && (
        <>
          {/* Change Image Button */}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center z-10">
            <Button
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleImageClick();
              }}
              disabled={isUploading}
              className="pointer-events-auto gap-2 bg-background/90 backdrop-blur-sm hover:bg-background border border-border/50"
            >
              {isUploading ? (
                <>
                  <Upload className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Camera className="w-4 h-4" />
                  Change Image
                </>
              )}
            </Button>
          </div>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
        </>
      )}
    </div>
  );
};

export default EditableImage;